import { memo, useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ReactApexChart from "react-apexcharts";
import i18next from "i18next";
import { MeasurementTypeI18N } from "@the-real-insight/tri-model";

import { Button } from "@/components/ui/button";
import MeasurementExportMenu from "./MeasurementExportMenu";
import { ChartSeries, partitionChartMetrics } from "./measurementSeries";
import {
  ZoomRange,
  coversFullRange,
  fullRangeOf,
  sameRange,
  zoomRangeFromEvent,
} from "./measurementZoom";

interface MeasurementChartListProperties {
  seriesList: Record<string, ChartSeries | null | undefined>;
  optionsList: Record<string, any>;
  /**
   * Identifies what the zoom belongs to (asset and selected period). When it changes, every
   * zoom is dropped; while it stays, zooms survive data refreshes.
   */
  resetKey?: string;
  /** Business id of the asset; part of the chart ids and, later, of the export file names. */
  assetId?: string;
}

const NO_ZOOM: Record<string, ZoomRange> = {};

const chartIdOf = (assetId: string, metric: string): string =>
  `measurement-${assetId}-${metric}`.replace(/[^A-Za-z0-9_-]+/g, "-");

interface ZoomState {
  key: string;
  byMetric: Record<string, ZoomRange>;
}

interface MeasurementChartProperties {
  base: any;
  series: ChartSeries;
  chartId: string;
  handlers: Record<string, (...args: any[]) => any>;
  zoom: ZoomRange | undefined;
}

/**
 * One chart. react-apexcharts compares its props deeply on every render of its parent and redraws
 * the whole chart when anything differs; the 1-second countdown of the tab used to trigger that for
 * all charts every second (tri-server #1636). Memoized on the inputs that really change - new data
 * (`base`, `series`) or this chart's own zoom - so the chart stays untouched in between.
 */
const MeasurementChart = memo(function MeasurementChart(properties: MeasurementChartProperties) {
  const { base, series, chartId, handlers, zoom } = properties;
  const options = useMemo(
    () => ({
      ...base,
      chart: {
        ...base.chart,
        id: chartId,
        events: { ...base.chart?.events, ...handlers },
        toolbar: {
          ...base.chart?.toolbar,
          // Our own export button replaces the small download icon (tri-server #1590).
          tools: { ...base.chart?.toolbar?.tools, download: false },
        },
      },
      xaxis: zoom ? { ...base.xaxis, min: zoom.min, max: zoom.max } : base.xaxis,
    }),
    [base, chartId, handlers, zoom],
  );
  const seriesArray = useMemo(() => [series], [series]);
  return <ReactApexChart options={options} series={seriesArray} type="area" width={400} height={250} />;
});

/**
 * The charts of the Measurements tab. Only metrics with data in the selected period get a chart;
 * the device-type catalogue also lists metrics a device never sends, and those are named in one
 * line instead of rendering empty charts (tri-server #1335). If nothing has data, a single hint
 * replaces the list.
 */
function MeasurementChartList(properties: MeasurementChartListProperties) {
  const { seriesList, optionsList, resetKey = "", assetId = "asset" } = properties;
  const { t } = useTranslation();
  const [zoomState, setZoomState] = useState<ZoomState>({ key: resetKey, byMetric: {} });
  // The chart keeps the event handlers of its first render, so they read the current values
  // from refs instead of closing over them.
  const resetKeyRef = useRef(resetKey);
  const optionsRef = useRef(optionsList);
  resetKeyRef.current = resetKey;
  optionsRef.current = optionsList;
  const zoomByMetric = zoomState.key === resetKey ? zoomState.byMetric : NO_ZOOM;

  const storeZoom = useCallback((metric: string, range: ZoomRange | null) => {
    setZoomState((previous) => {
      const base = previous.key === resetKeyRef.current ? previous.byMetric : NO_ZOOM;
      if (sameRange(base[metric], range ?? undefined)) {
        return previous.key === resetKeyRef.current ? previous : { key: resetKeyRef.current, byMetric: base };
      }
      const byMetric = { ...base };
      if (range) {
        byMetric[metric] = range;
      } else {
        delete byMetric[metric];
      }
      return { key: resetKeyRef.current, byMetric };
    });
  }, []);

  /** Chart events of a metric; created once per metric so the chart sees the same functions. */
  const handlersRef = useRef<Record<string, Record<string, (...args: any[]) => any>>>({});
  const handlersOf = (metric: string) => {
    if (!handlersRef.current[metric]) {
      // zoomed: drag zoom, toolbar zoom in/out and reset (reports the restored full range).
      // scrolled: panning.
      const onRange = (_chart: unknown, event: any) => {
        const range = zoomRangeFromEvent(event);
        const full = fullRangeOf(optionsRef.current[metric]);
        storeZoom(metric, range && !coversFullRange(range, full) ? range : null);
      };
      handlersRef.current[metric] = {
        zoomed: onRange,
        scrolled: onRange,
        // Without this the reset button would return to the zoomed range the options now carry.
        beforeResetZoom: () => {
          const full = fullRangeOf(optionsRef.current[metric]);
          return full ? { xaxis: { min: full.min, max: full.max } } : undefined;
        },
      };
    }
    return handlersRef.current[metric];
  };

  const { withData, withoutData } = partitionChartMetrics(seriesList, optionsList);
  const metricName = (key: string): string =>
    (MeasurementTypeI18N as any)[i18next.language]?.[key] ?? key;

  const exportMenu = (metric: string) => {
    const full = fullRangeOf(optionsList[metric]);
    const zoom = zoomByMetric[metric];
    const unit = String(optionsList[metric]?.yaxis?.[0]?.title?.text ?? "").trim();
    return (
      <MeasurementExportMenu
        chartId={chartIdOf(assetId, metric)}
        assetId={assetId}
        metric={metric}
        label={metricName(metric)}
        unit={unit}
        series={seriesList[metric] as ChartSeries}
        period={full ? { from: full.min, to: full.max } : { from: 0, to: Date.now() }}
        zoom={zoom ? { from: zoom.min, to: zoom.max } : null}
      />
    );
  };

  if (withData.length === 0 && withoutData.length > 0) {
    return (
      <div
        className="mt-4 flex h-[200px] items-center justify-center rounded-md border border-dashed text-sm text-muted-foreground"
        data-testid="no-measurements"
      >
        {t("assetPropertiesPanel.noMeasurementsInRange")}
      </div>
    );
  }

  return (
    <>
      {withoutData.length > 0 && (
        <div className="mt-4 text-xs text-muted-foreground" data-testid="metrics-without-data">
          {t("assetPropertiesPanel.metricsWithoutData", {
            metrics: withoutData.map(metricName).join(", "),
          })}
        </div>
      )}
      <div className="mt-4 flex min-w-0 flex-wrap gap-6">
        {withData.map((key: string) => (
          <div key={key} className="flex min-w-0 flex-col" data-testid={`measurement-chart-${key}`}>
            <div className="flex min-h-6 flex-wrap items-center gap-x-3">
              <div className="text-sm font-bold">{metricName(key)}</div>
              {zoomByMetric[key] && (
                <div className="flex items-center gap-2 text-xs text-muted-foreground" data-testid={`zoom-active-${key}`}>
                  <span>{t("assetPropertiesPanel.zoomActive")}</span>
                  <Button
                    type="button"
                    variant="link"
                    size="sm"
                    className="h-auto p-0 text-xs max-sm:h-11"
                    onClick={() => storeZoom(key, null)}
                  >
                    {t("assetPropertiesPanel.zoomReset")}
                  </Button>
                </div>
              )}
              <div className="ml-auto">{exportMenu(key)}</div>
            </div>
            <div className="overflow-x-auto">
              <MeasurementChart
                base={optionsList[key]}
                series={seriesList[key] as ChartSeries}
                chartId={chartIdOf(assetId, key)}
                handlers={handlersOf(key)}
                zoom={zoomByMetric[key]}
              />
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

export default memo(MeasurementChartList);
